/**
 * PR-D4-062 — Master split-screen container for Math + Verbal,
 * live exam and review modes.
 */

import type { ReactNode } from 'react';
import type { Question } from '../lib/types';
import { isEquationOnlySvg } from './NativeGraphRenderer';
import { KaTeXRenderer } from './KaTeXRenderer';
import './UniversalSplitLayout.css';

export type SplitLayoutMode = 'exam' | 'review';
export type StimulusKind = 'diagram' | 'passage' | 'stem' | 'none';

export interface UniversalSplitLayoutProps {
    mode?: SplitLayoutMode;
    stimulusKind: StimulusKind;
    header: ReactNode;
    stimulus?: ReactNode;
    children: ReactNode;
    className?: string;
}

export function getQuestionSvgFallback(question: Question): string | null {
    const graphData = question.graph_data as Record<string, unknown> | null;
    return (
        question.svg_data ||
        (typeof graphData?.svg === 'string' ? graphData.svg : null) ||
        null
    );
}

export function questionHasGraph(question: Question): boolean {
    const graphData = question.graph_data as Record<string, unknown> | null;
    const svgFallback = getQuestionSvgFallback(question);
    return Boolean(
        (svgFallback && !isEquationOnlySvg(svgFallback)) ||
            (Array.isArray(graphData?.expressions) && graphData.expressions.length > 0) ||
            (Array.isArray(graphData?.interactivePoints) && graphData.interactivePoints.length > 0),
    );
}

export function questionHasPassage(question: Question): boolean {
    return Boolean((question.passage ?? '').trim());
}

export function resolveStimulusKind(question: Question): StimulusKind {
    if (questionHasGraph(question)) return 'diagram';
    if (questionHasPassage(question)) return 'passage';
    return 'none';
}

function sanitizePassageHtml(html: string): string {
    return html
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
        .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
        .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
}

export function PassageContent({ text }: { text: string }) {
    const trimmed = text.trim();
    if (!trimmed) return null;
    if (/<table[\s>]/i.test(trimmed)) {
        return (
            <div
                className="universal-split__passage-text sat-passage__body--flow"
                dangerouslySetInnerHTML={{ __html: sanitizePassageHtml(trimmed) }}
            />
        );
    }
    return (
        <div className="universal-split__passage-text whitespace-pre-wrap">
            <KaTeXRenderer text={trimmed} />
        </div>
    );
}

export function questionHeaderLabel(questionNumber: number, totalQuestions: number): string {
    return `Question ${questionNumber} of ${totalQuestions}`;
}

export function UniversalSplitLayout({
    mode = 'exam',
    stimulusKind,
    header,
    stimulus,
    children,
    className = '',
}: UniversalSplitLayoutProps) {
    const hasStimulus = stimulusKind !== 'none' && Boolean(stimulus);

    return (
        <div
            data-testid="universal-split"
            className={[
                'universal-split',
                `universal-split--${mode}`,
                `universal-split--${stimulusKind}`,
                hasStimulus ? 'universal-split--dual' : 'universal-split--single',
                className,
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {hasStimulus ? (
                <aside
                    className={`universal-split__stimulus universal-split__stimulus--${stimulusKind}`}
                    data-testid="universal-split-stimulus"
                >
                    {stimulus}
                </aside>
            ) : null}
            <section className="universal-split__question" data-testid="universal-split-question">
                <header className="universal-split__header" data-testid="universal-split-header">
                    {header}
                </header>
                <div className="universal-split__body">{children}</div>
            </section>
        </div>
    );
}

export default UniversalSplitLayout;
